import { CircularProgress, Typography } from '@mui/material';
import classnames from 'classnames';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import ResizableHorizontalLayout from 'js/components/elements/ResizableHorizontalLayout';
import DataSourceDetails from 'js/components/data_sources/add_ds_form/DataSourceDetails/DataSourceDetails';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import {
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import {
  getFeatureFlagValue,
  isSourceFormRuntimeSelectionEnabled,
} from 'new/constants/featureFlags';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import { SUPPORT_KEY_SOURCE_FORM_REST_TEMP } from 'js/constants/supportPageKeys';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { formatCode, stringifyObject } from 'js/lib/FormatUtils';
import JSONUtils from 'js/lib/JSONUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import {
  debounce,
  filter,
  find,
  forEach,
  get,
  groupBy,
  has,
  identity,
  isEmpty,
  isEqual,
  map,
  mapValues,
  trim,
  values,
} from 'lodash';
import { HeaderField } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu';
import { UiMode } from 'new/constants/uiMode';
import { Button } from 'new/elements/support/Button';
import { tryCatchAsync } from 'new/utils/TryCatch';
import React from 'react';
import UIUtils from '../../../lib/UIUtils';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import DebouncedText from '../../elements/editors/mui/DebouncedText';
import FrequencyField from '../../elements/FrequencyField';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import UnsortedSelect from '../../elements/rselect/UnsortedSelect';
import ConfigureAPI from './ConfigureAPI';
import MonitoringInterval from './MonitoringInterval/MonitoringInterval';
import RuntimeSelection from 'new/modules/settings/capsules/components/RuntimeSelection/RuntimeSelection';
import RuntimeSelectionDescription from 'new/modules/settings/capsules/components/RuntimeSelection/RuntimeSelectionDescription';
import { ResponsePreviewPanel } from 'new/components/flows/configure/RestAPISource/redesign';

import './ConfigureTemplatizedAPI.scss';
import { MacroEditor, MacroEditorConfigProvider } from 'new/elements/MacroEditor';
import {
  extractDatasetFromApiServerFlow,
  extractMacrosFromApiServerFlow,
} from 'js/lib/MacroReplacementUtils';
import { getSourceEndpointOptions } from 'new/utils/actionEndpoints';

const CHECK_BOX_LABEL_STYLE = {
  fontSize: 12,
  fontWeight: 500,
  color: '#1F2F40',
  lineHeight: '24px',
  textTransform: 'initial',
  letterSpacing: '.5px',
};

const navOptions = [
  { label: 'Template', id: 'template-section' },
  { label: 'Schedule', id: 'scheduling-section' },
];

const getMacroSuggestions = (parentDataSet, parentDataSetSamples, editedSamples = null) => {
  if (!parentDataSet || !parentDataSetSamples) {
    return [];
  }

  try {
    // Extract parent dataset attribute macros for api_server flows
    const parentDataSetMacros = extractMacrosFromApiServerFlow(
      parentDataSet,
      parentDataSetSamples,
      editedSamples,
    );

    return !isEmpty(parentDataSetMacros) ? [...parentDataSetMacros] : [];
  } catch (error) {
    return [];
  }
};

/**
 * Rollout note: the Template-mode re-skin here is intentionally always-on. It is a
 * CSS/styling re-skin with a display-only response preview and is payload-identical,
 * so it ships to all orgs. Only the higher-risk Advanced step-list redesign is gated,
 * via the UI_ENABLE_REST_API_SOURCE_REDESIGN GrowthBook flag (see ConfigureAPI). This
 * asymmetry is by design.
 */
class ConfigureTemplatizedAPI extends React.Component {
  constructor(props) {
    super(props);

    const macroSuggestions = getMacroSuggestions(props.parentDataSet, props.parentDataSetSamples);

    this.state = {
      mode: 'simple',
      macroSuggestions,
      runtimeSectionVisible: true,
    };

    this._getSimpleModeContent = this._getSimpleModeContent.bind(this);
    this._getAdvancedModeContent = this._getAdvancedModeContent.bind(this);
    this._getEditorInputField = this._getEditorInputField.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._getSelectField = this._getSelectField.bind(this);
    this._handleModeChanged = this._handleModeChanged.bind(this);
    this._handleCronChanged = this._handleCronChanged.bind(this);
    this._handleAdvancedSettingChanged = this._handleAdvancedSettingChanged.bind(this);
    this._handleCallAPIClicked = this._handleCallAPIClicked.bind(this);
    this._getPathMatches = this._getPathMatches.bind(this);
    this._getResourceDataFields = this._getResourceDataFields.bind(this);
    this._getResourceDataFieldKey = this._getResourceDataFieldKey.bind(this);
    this._loadResourceDataFieldOptions = this._loadResourceDataFieldOptions.bind(this);
    this._handleSamplesChanged = this._handleSamplesChanged.bind(this);

    this._runtimeEnrichedAdvancedFieldsCache = null;
  }

  _handleRuntimeVisibilityChange = (visible) => {
    this.setState({ runtimeSectionVisible: visible });
  };

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_REST_TEMP);
  }

  componentDidMount() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
      selectedCredential,
      loadDataMaps,
    } = this.props;
    const sourceConfigFields = sourceConfigById[selectedConnection];

    if (find(sourceConfigFields, { type: 'data_map' })) {
      loadDataMaps(false, ACLQueryModeTypes.MODE_COLLABORATOR.key);
    }

    if (selectedCredential && !selectedCredential.credentials_non_secure_data) {
      this.props.getDataCredential(selectedCredential.id);
    }

    this._loadResourceDataFieldOptions();

    this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_REST_TEMP);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      paging: { metaInfoById, sourceConfigById },
      vendorEndpoints,
      selectedConnection,
      apiResponseByStage,
      templatizedAPIConfig,
      validateDataPath,
      parentDataSet,
      parentDataSetSamples,
    } = this.props;
    const { connectionTypeCategory } = metaInfoById[selectedConnection];
    const endpointId = templatizedAPIConfig.vendor_endpoint_id;

    // Update macro suggestions if parent dataset, samples, or edited samples changed
    const parentDataSetChanged = parentDataSet !== prevProps.parentDataSet;
    const parentDataSetSamplesChanged = parentDataSetSamples !== prevProps.parentDataSetSamples;
    const editedSamplesChanged = this.props.editedSamples !== prevProps.editedSamples;

    if (parentDataSetChanged || parentDataSetSamplesChanged || editedSamplesChanged) {
      const macroSuggestions = getMacroSuggestions(
        parentDataSet,
        parentDataSetSamples,
        this.props.editedSamples,
      );
      this.setState({ macroSuggestions });
    }

    if (connectionTypeCategory === CONNECTION_CATEGORY_REST) {
      // if call response changed, validate data path
      if (apiResponseByStage && apiResponseByStage !== prevProps.apiResponseByStage) {
        const endpointIds = { 0: endpointId };

        forEach(this._getResourceDataFields(), (field) => {
          endpointIds[this._getResourceDataFieldKey(field)] = field.vendor_endpoint_id;
        });

        forEach(endpointIds, (endpointId, key) => {
          if (
            apiResponseByStage[key] &&
            apiResponseByStage[key].apiResponseCode &&
            !isEqual(apiResponseByStage[key], prevProps.apiResponseByStage[key])
          ) {
            const endpoint = vendorEndpoints[endpointId];
            const iterations = get(endpoint, 'source_template["rest.iterations"]', []);
            const stageAPIConfig = iterations.length > 0 ? iterations[iterations.length - 1] : {};
            stageAPIConfig['response.data.path'] = UIUtils.replaceMacros({
              templateURL: stageAPIConfig['response.data.path'],
              config: templatizedAPIConfig,
              fields: sourceConfigById[selectedConnection],
            });
            validateDataPath(stageAPIConfig, key);
          }
        });
      }

      // if the newly selected endpoint has some vendor_resource_data params,
      // get data for them
      if (
        (endpointId && endpointId !== prevProps.templatizedAPIConfig.vendor_endpoint_id) ||
        sourceConfigById !== prevProps.paging.sourceConfigById
      ) {
        this._loadResourceDataFieldOptions();
      }
    }
  }

  _handleSamplesChanged(editedSamples) {
    if (this.props.onEditedSamplesChanged) {
      this.props.onEditedSamplesChanged(editedSamples);
    }
  }

  validateRequiredFields = (config) => {
    const {
      paging: { sourceConfigById },
      selectedConnection,
    } = this.props;
    const fields = sourceConfigById[selectedConnection];
    return getEmptyRequiredFields(fields, config);
  };

  onSaveAdvancedMode = async (sourceConfigString) => {
    const { onError, nextFormStage, onSaveAdvancedMode } = this.props;

    if (!JSONUtils.isJSON(sourceConfigString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const parsed = JSON.parse(sourceConfigString);
    const { name, description, template_config, source_config, ...rest } = parsed;
    const config = source_config ?? template_config;

    // NEX-21470: preserve any top-level keys the user pasted alongside
    // template_config/source_config (e.g. vendor_endpoint_id) instead of
    // silently dropping them.
    const data = {
      ...rest,
      ...config,
      name: name || '',
      description: description || '',
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));

    if (error) {
      onError?.(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sourceConfig, onSourceConfigChanged } = this.props;
    if (typeof onSourceConfigChanged !== 'function') return;

    onSourceConfigChanged({ ...sourceConfig, 'ui.mode': UiMode.VIEW });
  };

  getResourceConfig = () => {
    const { templatizedAPIConfig, apiConfig } = this.props;
    const isaAdvancedTemplatizedAPI = !!apiConfig?.advanced_templatized_api;

    const clonedConfig = isaAdvancedTemplatizedAPI
      ? structuredClone(apiConfig)
      : structuredClone(templatizedAPIConfig);

    const name = this.props.sourceName || clonedConfig.name || '';
    const description = this.props.sourceDescription || clonedConfig.description || '';

    delete clonedConfig.name;
    delete clonedConfig.description;
    delete clonedConfig.sourceName;
    delete clonedConfig.sourceDescription;

    return {
      name,
      description,
      source_config: clonedConfig,
    };
  };

  render() {
    const {
      apiConfig,
      onInputChange,
      selectedConnection,
      sourceName,
      sourceDescription,
      selectedConnectionDetails = {},
      parentDataSet,
    } = this.props;

    const resourceConfig = this.getResourceConfig();
    const isAdvancedMode = apiConfig?.advanced_templatized_api;

    const configureTemplatizedAPIContent = (
      <div
        className={classnames('configure-templatized-api', {
          'api-server-target': !!parentDataSet,
        })}
      >
        <div className="form-section-tabs-container">
          <div
            data-mode="simple"
            className={classnames('form-section-tab', {
              selected: !isAdvancedMode,
            })}
            onClick={this._handleModeChanged}
            data-testid="simple-tab"
          >
            <h1>Template</h1>
          </div>
          <div
            data-mode="advanced"
            className={classnames('form-section-tab', {
              selected: isAdvancedMode,
            })}
            onClick={this._handleModeChanged}
            data-testid="advanced-tab"
          >
            <h1>Advanced</h1>
          </div>
        </div>

        <div
          id="configure-templatized-api-container"
          className="configure-templatized-api-container advanced-configuration-left-content"
        >
          <div className="form-section-tab-content">
            {isAdvancedMode ? this._getAdvancedModeContent() : this._getSimpleModeContent()}
          </div>
        </div>
      </div>
    );

    return (
      <div className="api-server-target-templatized-api-container">
        <DataSourceDetails
          name={sourceName}
          description={sourceDescription}
          onInputChange={onInputChange}
          leftView={
            <HeaderMetaConfiguration
              enableInputFields={false}
              isDestination={false}
              connection={selectedConnection}
              sourceOrDestinationChipChildren={
                parentDataSet ? null : (
                  <ResourcePopoverMenu
                    isTemplatized
                    resourceConfig={resourceConfig}
                    connectorType={selectedConnection}
                    jsonConfigOnSave={this.onSaveAdvancedMode}
                    jsonConfigOnClose={this.props.goBack}
                    onSwitchUiMode={this.onSwitchUiMode}
                    /* Template mode is the simplified view — View JSON Config is offered
                       only in Advanced mode (matches develop; the raw config editor is an
                       Advanced-mode affordance). */
                    disabledActions={isAdvancedMode ? [] : ['view-json-config']}
                    resourcePersisted={!!this.props.sourceBeingEdited?.id}
                  />
                )
              }
            />
          }
        />

        {parentDataSet ? (
          <div className="api-server-target-horizontal-layout">
            <ResizableHorizontalLayout initialSizes={['25%', '75%']} minSizes={[360, 400]}>
              <div className="parent-data-set-container">
                <DataSetDataBrowserContainer
                  data={parentDataSet}
                  defaultViewMode={SchemaEditorViewModeType.CODE}
                  compactHeader
                  darkEditor
                  allowEditing
                  onSamplesChanged={this._handleSamplesChanged}
                />
              </div>
              {configureTemplatizedAPIContent}
            </ResizableHorizontalLayout>
          </div>
        ) : (
          configureTemplatizedAPIContent
        )}
      </div>
    );
  }

  _getSimpleModeContent() {
    const {
      paging: { sourceConfigById, metaInfoById },
      dataMaps,
      vendorEndpoints,
      selectedConnection,
      templatizedAPIConfig,
      apiResponse,
      isLoadingProbeSample,
      dataPathValidationResultByStage,
      visibleFieldData,
      parentDataSet,
    } = this.props;

    const sourceConfigFields = sourceConfigById[selectedConnection];
    const fieldsByIsEndpoint = groupBy(
      sourceConfigFields,
      (field) => field.name === 'vendor_endpoint_id',
    );
    const endpointField = fieldsByIsEndpoint[true]?.[0];
    const otherFields = fieldsByIsEndpoint[false];
    let visibleFields = filter(otherFields, (field) =>
      UIUtils.shouldRenderField(field, templatizedAPIConfig),
    );
    const visibleFieldsByHasSection = groupBy(visibleFields, (field) => !!field.section);
    visibleFields = visibleFieldsByHasSection[false];

    let dataMapOptions;
    let dataMapColumnOptions = {};

    if (find(visibleFields, { type: 'data_map' })) {
      dataMapOptions = map(values(dataMaps), (value) => ({
        label: value.name,
        value: value.id,
      }));

      const dataMapColumnFields = filter(visibleFields, {
        type: 'data_map_column',
      });

      forEach(dataMapColumnFields, (field) => {
        const dataMapId = templatizedAPIConfig[field.dataMapField];
        const dataMap = dataMapId && dataMaps[dataMapId];

        if (dataMap) {
          dataMapColumnOptions[field.name] = map(
            dataMap.map_entry_schema.properties,
            (def, name) => ({
              label: name,
              value: name,
            }),
          );
        }
      });
    }

    const selectedEndpointId = templatizedAPIConfig?.vendor_endpoint_id;
    const selectedEndpoint = selectedEndpointId && vendorEndpoints[selectedEndpointId];

    // Response rendering (incl. failures) is handled by the redesigned
    // ResponsePreviewPanel, which reads the probe response directly.
    const connectionNameLabel =
      selectedConnection && has(metaInfoById, selectedConnection)
        ? metaInfoById[selectedConnection].displayName
        : 'API';

    const cronField = sourceConfigById.rest?.filter((field) => field.name === 'start.cron')?.[0];

    if (!endpointField) {
      return null;
    }
    //
    const effectiveFlowType = this.props.flowType || 'streaming';
    const showRuntimeSelection =
      !parentDataSet &&
      isSourceFormRuntimeSelectionEnabled(
        getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS'),
        getFeatureFlagValue('UI_ENABLE_RUNTIME_SELECTION'),
        CONNECTION_CATEGORY_TEMPLATIZED_API,
        this.props.flowType,
      );
    const effectiveNavOptions =
      showRuntimeSelection && this.state.runtimeSectionVisible
        ? [
            { label: 'Template', id: 'template-section' },
            { label: 'Select Runtime', id: 'select-runtime-section' },
            { label: 'Schedule', id: 'scheduling-section' },
          ]
        : navOptions;

    return (
      <NavScroller
        navOptions={parentDataSet ? [] : effectiveNavOptions}
        navClassName={parentDataSet ? '' : 'tmpl-redesign-nav'}
        style={parentDataSet ? { height: '100%' } : {}}
        isColumnLayout={!!parentDataSet}
      >
        <NavScrollerContext.Consumer>
          {(consumer) => {
            return (
              <div className="nav-scroller-container">
                <div
                  id="template-section"
                  className={classnames({
                    'configure-templatized-api': true,
                    'configure-template': true,
                    'configure-templatized-api-focused':
                      consumer?.activeScroll === 'template-section',
                  })}
                >
                  <div className="tmpl-left-col">
                    <section className="configuration-holder">
                      <div className="body-section">
                        <p className="description">
                          Predefined {`${connectionNameLabel}`} endpoints, or Advanced mode for a
                          custom API.
                        </p>
                        <ol>
                          <li>
                            <div className="input-row">
                              <div className="input-holder">
                                <label htmlFor="vendor_endpoint_id" className="endpoint-label">
                                  Endpoint
                                </label>
                                {this._getSelectField(
                                  'vendor_endpoint_id',
                                  this._getEndpointOptions(endpointField),
                                  {
                                    unsorted: true,
                                  },
                                )}
                              </div>
                              <div className="call-api-button-container call-soap-button-container">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={this._handleCallAPIClicked}
                                  className={classnames('call-api-button', {
                                    loading: isLoadingProbeSample,
                                  })}
                                  data-testid="call-api-button"
                                >
                                  {isLoadingProbeSample ? (
                                    <CircularProgress
                                      size={19}
                                      sx={{
                                        color: 'var(--color-danger-700)',
                                      }}
                                    />
                                  ) : (
                                    'Test'
                                  )}
                                </Button>
                              </div>
                            </div>
                            {selectedEndpoint &&
                              (selectedEndpoint.description ||
                                (has(selectedEndpoint, 'config.info_url') &&
                                  selectedEndpoint.config.info_url)) && (
                                <div className="data-path-suggestions">
                                  <h4>Details</h4>
                                  <div className="suggestions-container endpoint-info-container">
                                    {selectedEndpoint.description && (
                                      <div>
                                        {UIUtils.stringToParagraphs(selectedEndpoint.description)}
                                      </div>
                                    )}
                                    {has(selectedEndpoint, 'config.info_url') &&
                                      selectedEndpoint.config.info_url && (
                                        <a
                                          className="endpoint-doc-link undecorated-link"
                                          href={selectedEndpoint.config.info_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Open Documentation
                                        </a>
                                      )}
                                  </div>
                                </div>
                              )}
                          </li>
                          {!isEmpty(visibleFields) &&
                            map(visibleFields, (field) => {
                              let fieldComponent;
                              const wrappedLabel = (
                                <HeaderField
                                  item={field}
                                  showRequiredIndicator
                                  className="endpoint-label"
                                />
                              );

                              if (!isEmpty(field.options)) {
                                fieldComponent = (
                                  <div className="input-holder">
                                    {wrappedLabel}
                                    {this._getSelectField(field.name, field.options)}
                                  </div>
                                );
                              } else if (field.type === 'frequency') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    <FrequencyField
                                      name={field.name}
                                      label="Fetch Data"
                                      value={templatizedAPIConfig[field.name]}
                                      labelStyle={CHECK_BOX_LABEL_STYLE}
                                      iconColor="#4AD496"
                                      onChange={this._handleCronChanged}
                                    />
                                  </div>
                                );
                              } else if (field.type === 'data_map') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    {wrappedLabel}
                                    {this._getSelectField(field.name, dataMapOptions)}
                                  </div>
                                );
                              } else if (field.type === 'data_map_column') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    {wrappedLabel}
                                    {this._getSelectField(
                                      field.name,
                                      dataMapColumnOptions[field.name] || [],
                                    )}
                                  </div>
                                );
                              } else if (field.type === 'vendor_resource_data') {
                                const fieldKey = this._getResourceDataFieldKey(field);
                                const data = get(
                                  dataPathValidationResultByStage,
                                  `${fieldKey}['response.data.path'].matches`,
                                );
                                const options = map(
                                  filter(data, (item) =>
                                    has(item, field.option_property_mapping.value),
                                  ),
                                  (item) =>
                                    mapValues(field.option_property_mapping, (propertyPath) =>
                                      get(
                                        item,
                                        propertyPath,
                                        get(item, field.option_property_mapping.value),
                                      ),
                                    ),
                                );

                                fieldComponent = (
                                  <div className="input-holder">
                                    {wrappedLabel}
                                    {this._getSelectField(field.name, options, {
                                      isCreatable: true,
                                      formatCreateLabel: identity,
                                      isDisabled: isLoadingProbeSample,
                                      placeholder: 'Select or type...',
                                    })}
                                  </div>
                                );
                              } else if (field.type === 'sql_editor') {
                                fieldComponent = (
                                  <div className="input-holder">
                                    {wrappedLabel}
                                    {this._getEditorInputField(field.name, {})}
                                  </div>
                                );
                              } else {
                                fieldComponent = (
                                  <div className="input-holder">
                                    {wrappedLabel}
                                    {this._getTextField(
                                      field.name,
                                      field.inputType === 'textarea'
                                        ? {
                                            multiline: true,
                                            minRows: 4,
                                            maxRows: 8,
                                          }
                                        : {},
                                      true,
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div key={field.name} className="input-row">
                                  {fieldComponent}
                                </div>
                              );
                            })}
                        </ol>
                      </div>
                    </section>

                    {showRuntimeSelection && this.state.runtimeSectionVisible && (
                      <div
                        id="select-runtime-section"
                        aria-label="section-container"
                        className={classnames({
                          runtime_selection_section_card: true,
                          runtime_selection_section_card_flush: true,
                          runtime_selection_section_card_focused:
                            consumer?.activeScroll === 'select-runtime-section',
                        })}
                      >
                        <h2 className="runtime_selection_section_heading">
                          {this.props.runtimeSelectionDisabled
                            ? 'Selected Runtime'
                            : 'Select Runtime'}
                        </h2>
                        <RuntimeSelectionDescription
                          readOnly={this.props.runtimeSelectionDisabled}
                        />
                        <div className="runtime_selection_section_body">
                          <RuntimeSelection
                            flowType={effectiveFlowType}
                            connectionType={CONNECTION_CATEGORY_TEMPLATIZED_API}
                            onSelectRuntime={this.props.onSelectRuntime}
                            disabled={this.props.runtimeSelectionDisabled}
                            selectedClusterId={this.props.selectedClusterId}
                            onVisibilityChange={this._handleRuntimeVisibilityChange}
                          />
                        </div>
                      </div>
                    )}

                    {!parentDataSet && (
                      <div id="scheduling-section">
                        <MonitoringInterval
                          field={cronField}
                          source={this.props.sourceBeingEdited}
                          config={visibleFieldData}
                          monitorIntervalContainerClassname={classnames({
                            frequency_style: true,
                            frequency_focused_style:
                              consumer?.activeScroll === 'scheduling-section',
                          })}
                          onConfigChanged={this._handleAdvancedSettingChanged}
                          showExternalTrigger={this.props.showExternalTrigger}
                        />
                      </div>
                    )}
                  </div>

                  <section className="api-response-holder api-response-holder--redesigned">
                    <ResponsePreviewPanel
                      dataTabs={false}
                      apiResponse={apiResponse}
                      stageConfig={{
                        ...templatizedAPIConfig,
                        contentType: apiResponse?.apiResponseContentType,
                      }}
                      onChange={(name, value) => onTemplatizedAPIConfigChanged(name, value)}
                    />
                  </section>
                </div>
              </div>
            );
          }}
        </NavScrollerContext.Consumer>
      </NavScroller>
    );
  }

  _getAdvancedModeContent() {
    const {
      paging,
      dataMaps,
      dataMapSamples,
      selectedConnection,
      apiConfig,
      apiResponseByStage,
      apiResponseUpdateTime,
      isLoadingProbeSample,
      dataPathValidationResultByStage,
      validateDataPath,
      loadDataMaps,
      setFormError,
      onClearAPIResponse,
      onCallRESTAPI,
      onAPIConfigChanged,
      onGetParameterizedURLs,
      onAddStep,
      onRemoveStep,
      advancedFields,
      onSourceConfigChanged,
      visibleFieldData,
      parentDataSet,
      parentDataSetSamples,
    } = this.props;

    const shouldInjectRuntimeField =
      !parentDataSet &&
      isSourceFormRuntimeSelectionEnabled(
        getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS'),
        getFeatureFlagValue('UI_ENABLE_RUNTIME_SELECTION'),
        CONNECTION_CATEGORY_TEMPLATIZED_API,
        this.props.flowType,
      );

    const runtimeSelectionField = { name: 'runtime_selection', type: 'runtime_selection' };
    const hasRuntimeInAdvancedFields = advancedFields?.some((f) => f.type === 'runtime_selection');
    const flowTypeProp = this.props.flowType;
    let enrichedAdvancedFields;
    if (hasRuntimeInAdvancedFields) {
      enrichedAdvancedFields = advancedFields;
    } else if (shouldInjectRuntimeField) {
      const cache = this._runtimeEnrichedAdvancedFieldsCache;
      if (
        cache &&
        cache.advancedFields === advancedFields &&
        cache.parentDataSet === parentDataSet &&
        cache.flowType === flowTypeProp
      ) {
        enrichedAdvancedFields = cache.enriched;
      } else {
        enrichedAdvancedFields = [...(advancedFields || []), runtimeSelectionField];
        this._runtimeEnrichedAdvancedFieldsCache = {
          advancedFields,
          parentDataSet,
          flowType: flowTypeProp,
          enriched: enrichedAdvancedFields,
        };
      }
    } else {
      enrichedAdvancedFields = advancedFields;
    }

    return (
      <ConfigureAPI
        isTemplatizedAPI
        removeHelperSection
        visibleFieldData={visibleFieldData}
        showAdvancedSettings={false}
        sourceBeingEdited={this.props.sourceBeingEdited}
        paging={paging}
        dataMaps={dataMaps}
        dataMapSamples={dataMapSamples}
        selectedConnection={selectedConnection}
        apiConfig={apiConfig}
        apiResponseByStage={apiResponseByStage}
        apiResponseUpdateTime={apiResponseUpdateTime}
        isLoadingProbeSample={isLoadingProbeSample}
        dataPathValidationResultByStage={dataPathValidationResultByStage}
        validateDataPath={validateDataPath}
        loadDataMaps={loadDataMaps}
        setFormError={setFormError}
        onClearAPIResponse={onClearAPIResponse}
        onCallAPI={onCallRESTAPI}
        onAPIConfigChanged={onAPIConfigChanged}
        onGetParameterizedURLs={onGetParameterizedURLs}
        onAddStep={onAddStep}
        onRemoveStep={onRemoveStep}
        setContextualSupport={this.props.setContextualSupport}
        unsetContextualSupport={this.props.unsetContextualSupport}
        advancedFields={enrichedAdvancedFields}
        onSourceConfigChanged={onSourceConfigChanged}
        showExternalTrigger={this.props.showExternalTrigger}
        parentDataSet={parentDataSet}
        parentDataSetSamples={parentDataSetSamples}
        editedSamples={this.props.editedSamples}
        onEditedSamplesChanged={this.props.onEditedSamplesChanged}
        flowType={this.props.flowType || 'streaming'}
        connectionType={CONNECTION_CATEGORY_TEMPLATIZED_API}
        sourceConfig={this.props.sourceConfig}
        onSelectRuntime={this.props.onSelectRuntime}
        runtimeSelectionDisabled={this.props.runtimeSelectionDisabled}
        selectedClusterId={this.props.selectedClusterId}
        credentialData={
          this.props.selectedCredential
            ? {
                ...(this.props.selectedCredential.template_config || {}),
                ...(this.props.selectedCredential.verified_config || {}),
                ...(this.props.selectedCredential.credentials_non_secure_data || {}),
              }
            : null
        }
      />
    );
  }

  _getEditorInputField(name) {
    const { templatizedScriptConfig, onTemplatizedScriptConfigChanged } = this.props;

    return (
      <LoadableMonacoEditor
        language="sql"
        value={get(templatizedScriptConfig, name, '')}
        onChange={debounce((text) => onTemplatizedScriptConfigChanged(name, text), 200)}
        theme="dark"
        height={250}
        options={{
          minimap: { enabled: false },
        }}
      />
    );
  }

  _getTextField(name, props = {}, useMacros = false) {
    const {
      templatizedAPIConfig,
      onTemplatizedAPIConfigChanged,
      isAPIServerTarget,
      parentDataSet,
      parentDataSetSamples,
      onGetParameterizedURLs,
      editedSamples = null,
    } = this.props;

    const shouldUseMacros = useMacros && isAPIServerTarget;

    if (shouldUseMacros) {
      return (
        <MacroEditorConfigProvider
          dateFormat={templatizedAPIConfig['date.format'] || 'yyyy-MM-dd'}
          dateTimeUnit={templatizedAPIConfig['date.time.unit'] || 'dd'}
          parentDatasetSamples={extractDatasetFromApiServerFlow(
            parentDataSet,
            parentDataSetSamples,
            editedSamples,
          )}
          parameterizedURLs={onGetParameterizedURLs ? onGetParameterizedURLs(0) : []}
        >
          <MacroEditor
            multiline={props.multiline || false}
            rows={props.rows || 1}
            maxRows={props.maxRows}
            value={templatizedAPIConfig[name] || ''}
            onChange={(newValue) => onTemplatizedAPIConfigChanged(name, trim(newValue))}
            suggestions={this.state.macroSuggestions || []}
            placeholder={props.placeholder}
          />
        </MacroEditorConfigProvider>
      );
    }

    return (
      <DebouncedText
        id={name}
        value={get(templatizedAPIConfig, name, '')}
        onChange={(text) => onTemplatizedAPIConfigChanged(name, trim(text))}
        {...props}
      />
    );
  }

  // Action endpoints are only offered for API Server targets; ETL sources hide them, except the
  // endpoint an edited source already uses (or has selected).
  _getEndpointOptions(endpointField) {
    const { flowType, isAPIServerTarget, sourceBeingEdited, templatizedAPIConfig } = this.props;
    return getSourceEndpointOptions(endpointField?.options, {
      flowType,
      isAPIServerTarget,
      sourceBeingEdited,
      selectedEndpointId: templatizedAPIConfig?.vendor_endpoint_id,
    });
  }

  _getSelectField(name, options, customProps = {}) {
    const { templatizedAPIConfig, onTemplatizedAPIConfigChanged } = this.props;
    const currentValue = getCurrentSelectedOption(
      options,
      templatizedAPIConfig[name],
      null,
      null,
      !!customProps.isCreatable,
    );

    const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;

    const { unsorted, ...selectProps } = customProps;
    const SelectComponent = unsorted ? UnsortedSelect : SortedSelect;

    return (
      <SelectComponent
        id={name}
        value={currentValue}
        options={options}
        menuPlacement="auto"
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        menuPortalTarget={menuPortalTarget}
        onChange={(currValue) => {
          const newValue = getNewSelectionValue(currValue, true);
          onTemplatizedAPIConfigChanged(name, newValue);
        }}
        className="dropdown"
        classNamePrefix="tmpl-endpoint-select"
        styles={{
          ...redesignStyles,
          control: (base) => base,
        }}
        {...selectProps}
      />
    );
  }

  _handleModeChanged(event) {
    const { apiConfig, onClearAPIResponse, onAPIConfigChanged } = this.props;
    const { mode } = event.currentTarget.dataset;
    const isAdvancedMode = apiConfig && apiConfig.advanced_templatized_api;
    const isNewAdvancedMode = mode === 'advanced';

    if ((isAdvancedMode && !isNewAdvancedMode) || (!isAdvancedMode && isNewAdvancedMode)) {
      onClearAPIResponse();
    }

    onAPIConfigChanged('advanced_templatized_api', isNewAdvancedMode);
  }

  _handleCronChanged(value, fieldName) {
    this.props.onTemplatizedAPIConfigChanged(fieldName, value);
  }

  _handleAdvancedSettingChanged(data, name, value) {
    this.props.onTemplatizedAPIConfigChanged(name, value, null, data);
    this.props.onAPIConfigChanged(name, value);
  }

  _handleCallAPIClicked() {
    this.props.onCallAPI();
  }

  _getPathMatches() {
    const { dataPathValidationResult, apiResponse } = this.props;
    const matches = get(dataPathValidationResult, '["response.data.path"].matches');

    if (!matches) {
      return null;
    }

    const contentType = get(apiResponse, 'apiResponseContentType');
    const matchesString = stringifyObject(matches, contentType);

    return formatCode(matchesString, contentType);
  }

  _getResourceDataFields() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
      templatizedAPIConfig,
    } = this.props;
    const sourceConfigFields = sourceConfigById[selectedConnection];

    return filter(
      sourceConfigFields,
      (field) =>
        field.type === 'vendor_resource_data' &&
        UIUtils.shouldRenderField(field, templatizedAPIConfig),
    );
  }

  _getResourceDataFieldKey(field) {
    return `vendor_resource_data_${field.param_id}`;
  }

  _loadResourceDataFieldOptions() {
    const {
      paging: { metaInfoById },
      selectedConnection,
      selectedCredentialId,
      templatizedAPIConfig,
      apiResponseByStage,
      probeGetSample,
    } = this.props;
    const { connectionTypeCategory } = metaInfoById[selectedConnection];
    const endpointId = templatizedAPIConfig.vendor_endpoint_id;

    if (endpointId && connectionTypeCategory === CONNECTION_CATEGORY_REST) {
      forEach(this._getResourceDataFields(), (field) => {
        const fieldKey = this._getResourceDataFieldKey(field);

        if (!apiResponseByStage[fieldKey]) {
          probeGetSample(selectedCredentialId, fieldKey, {
            vendor_endpoint_id: field.vendor_endpoint_id,
            template_config: field.template_config,
            resource_type: 'SOURCE',
          });
        }
      });
    }
  }
}

export default ConfigureTemplatizedAPI;
